import { PossibleAnswerDto } from 'api/survey';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { FC, useMemo } from 'react';
import { QuickSurveyAnswerSelectProps } from './quickSurvey.types';

export const QuickSurveyAnswerSelect: FC<QuickSurveyAnswerSelectProps> = ({
  answers,
  selectedAnswerId,
  onSelect,
  placeholder = 'Sélectionner une réponse'
}) => {
  const selectedAnswer = useMemo(
    () => answers.find((a) => a.id === selectedAnswerId) || null,
    [answers, selectedAnswerId]
  );

  return (
    <Listbox
      as="div"
      value={selectedAnswer}
      onChange={(answer) => answer && onSelect(answer)}
      className="w-full relative"
    >
      {({ open }) => (
        <>
          <ListboxButton
            className={clsx(
              'w-full text-sm flex justify-between items-center border border-gray-300 px-4 py-3 rounded-lg bg-white cursor-pointer transition-colors duration-200',
              !selectedAnswer && 'text-gray-400',
              selectedAnswer && 'text-primary font-medium',
              open && 'border-primary'
            )}
          >
            <span className="truncate">
              {selectedAnswer?.label || placeholder}
            </span>
            <ChevronDown
              className={clsx(
                'w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0',
                open && 'rotate-180 text-primary'
              )}
            />
          </ListboxButton>

          <ListboxOptions
            // 'bottom end' : le menu s'aligne sur le bord droit du champ et s'élargit vers
            // la gauche -> les options longues restent visibles, même dans un panneau latéral.
            anchor={{ to: 'bottom end', gap: 4, padding: 16 }}
            className={clsx(
              'min-w-[var(--button-width)] w-max max-w-[90vw] bg-white z-50 overflow-y-auto border border-gray-300 rounded-lg shadow-lg',
              '[--anchor-max-height:60vh]'
            )}
          >
            {answers.map((answer) => (
              <ListboxOption
                key={answer.id}
                value={answer}
                className={({ focus, selected }) =>
                  clsx(
                    'px-4 py-3 cursor-pointer text-sm transition-colors',
                    'border-b border-gray-100 last:border-b-0',
                    selected
                      ? 'bg-primary text-white font-medium'
                      : focus
                        ? 'bg-primary/10 text-primary'
                        : 'text-gray-700 hover:bg-gray-50'
                  )
                }
              >
                {answer.label}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};
